import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconDirectionDown,
} from 'components/icons';
import { useOutsideClick } from 'hooks';
import { map, padStart, range, split, toNumber, toString } from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { MouseEvent, useRef, useState } from 'react';
import { DayPickerSingleDateController } from 'react-dates';
import { useFormContext, useWatch } from 'react-hook-form';
import { FreeMode, Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';

interface Props {
  name: string;
  disabled?: boolean;
  required?: boolean;
  placeHolder?: boolean;
  label?: string;
  error?: { message?: string };
  onChange?: (value?: string) => void;
}

const FormDatetime = ({ name, disabled, required, placeHolder, label, error, onChange }: Props) => {
  const form = useFormContext<any>();
  const valueWatch = useWatch({ control: form.control, name });
  const datetimeParts = valueWatch ? split(valueWatch, 'T') : null;
  const [isOpen, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [hourSwiper, setHourSwiper] = useState<SwiperType>();
  const [minuteSwiper, setMinuteSwiper] = useState<SwiperType>();

  const clickOutside = () => {
    if (!disabled) {
      setOpen(!isOpen);
    }
  };
  const selectDate = (value: Moment | null) => {
    const newValue = `${value?.format(`YYYY-MM-DD`)}T${
      datetimeParts?.[1] ? datetimeParts[1] : '00:00'
    }`;
    form.setValue(name, newValue, { shouldValidate: true });
    onChange?.(newValue);
  };
  const swiperHour = (swiper: SwiperType) => {
    setHourSwiper(swiper);
  };
  const changeHour = (swiper: SwiperType) => {
    const newMoment = moment(
      new Date(
        0,
        0,
        0,
        toNumber(padStart(toString(swiper.realIndex), 2, '0')),
        datetimeParts?.[1] ? toNumber(split(datetimeParts[1], ':')[1]) : 0,
      ),
    );
    const newValue = `${
      datetimeParts?.[0] ? datetimeParts[0] : moment(moment.now()).format(`YYYY-MM-DD`)
    }T${newMoment?.format(`HH:mm`)}`;
    form.setValue(name, newValue, { shouldValidate: true });
    onChange?.(newValue);
  };
  const swiperMinute = (swiper: SwiperType) => {
    setMinuteSwiper(swiper);
  };
  const changeMinute = (swiper: SwiperType) => {
    const newMoment = moment(
      new Date(
        0,
        0,
        0,
        datetimeParts?.[1] ? toNumber(split(datetimeParts[1], ':')[0]) : 0,
        toNumber(padStart(toString(swiper.realIndex), 2, '0')),
      ),
    );
    const newValue = `${
      datetimeParts ? datetimeParts[0] : moment(moment.now()).format(`YYYY-MM-DD`)
    }T${newMoment?.format(`HH:mm`)}`;
    form.setValue(name, newValue, { shouldValidate: true });
    onChange?.(newValue);
  };
  const clear = (event: MouseEvent<HTMLButtonElement>) => {
    const newValue = undefined;
    event.stopPropagation();
    if (hourSwiper) {
      hourSwiper.slideTo(1, 0);
    }
    if (minuteSwiper) {
      minuteSwiper.slideTo(1, 0);
    }
    form.setValue(name, newValue, { shouldValidate: true });
    onChange?.(newValue);
  };

  useOutsideClick(containerRef, () => {
    setOpen(false);
  });

  return (
    <div
      ref={containerRef}
      className={`flex select-none flex-col space-y-[3px] ${disabled ? '' : 'cursor-text'}`}
      onClick={clickOutside}
    >
      <div className={disabled ? 'pointer-events-none' : ''}>
        <fieldset
          className={`group relative flex items-center rounded-[8px] border px-[12px] h-[40px] ${
            error ? 'border-alert' : isOpen ? 'border-primary' : 'border-stroke'
          } ${disabled ? 'bg-disabled' : 'bg-paper'}`}
        >
          {isOpen || valueWatch ? (
            !placeHolder && (
              <legend className='pointer-events-none mx-[2px] px-[4px] leading-[2px]'>
                <label className='text-[12px]'>
                  <span className='text-text2'>{label}</span>{' '}
                  {required && <span className='text-alert'>*</span>}
                </label>
              </legend>
            )
          ) : (
            <label className='pointer-events-none absolute top-1/2 -translate-y-1/2'>
              <span className='text-text2'>{label}</span>{' '}
              {required && <span className='text-alert'>*</span>}
            </label>
          )}
          <div className='flex w-full items-center justify-between space-x-[8px]'>
            <div className='flex w-full items-center'>
              <span className='pointer-events-none w-full line-clamp-1'>
                {valueWatch ? moment(valueWatch).format('HH:mm, DD/MM/YYYY') : ''}
              </span>
              {!isOpen && valueWatch && (
                <button type='button' className='ml-[8px] hidden group-hover:flex' onClick={clear}>
                  <IconClose className='h-[20px] w-[20px] text-text2' />
                </button>
              )}
            </div>
            <div className='broder-stroke h-full border-l pl-[8px]'>
              <IconCalendar className='text-text2' />
            </div>
          </div>
          {isOpen && (
            <div
              className='absolute left-0 top-full z-[2] pt-[4px] mt-[12px]'
              onClick={(event) => event.stopPropagation()}
            >
              <div className='rounded-[8px] bg-paper py-[16px] shadow-4'>
                <div className='flex'>
                  <div className='flex flex-col space-y-[12px]'>
                    <div className='DesktopDayPicker overflow-hidden w-full'>
                      <DayPickerSingleDateController
                        hideKeyboardShortcutsPanel
                        date={valueWatch ? moment(valueWatch) : null}
                        focused={focused}
                        firstDayOfWeek={0}
                        numberOfMonths={1}
                        daySize={24}
                        transitionDuration={0}
                        monthFormat='[Tháng] M [năm] YYYY'
                        navNext={<IconChevronRight className='w-[20px] h-[20px]' />}
                        navPrev={<IconChevronLeft className='w-[20px] h-[20px]' />}
                        initialVisibleMonth={() => moment()}
                        renderDayContents={(day) => (
                          <div className='CalendarDay__div'>
                            <div className='CalendarDay__div'>{day.format('D')}</div>
                          </div>
                        )}
                        onFocusChange={({ focused }) => setFocused(focused)}
                        onDateChange={selectDate}
                      />
                    </div>
                    <div className='flex justify-center items-center space-x-[16px]'>
                      <div className='flex items-center space-x-[8px]'>
                        <span className='bg-alert rounded-full w-[12px] h-[12px]' />
                        <span className='text-[12px] leading-[15px]'>Hôm nay</span>
                      </div>
                      <div className='flex items-center space-x-[8px]'>
                        <span className='bg-primary rounded-full w-[12px] h-[12px]' />
                        <span className='text-[12px] leading-[15px]'>Ngày đang chọn</span>
                      </div>
                    </div>
                  </div>
                  <div className='border-x border-dashed w-[4px]' />
                  <div className='flex items-center space-x-[24px] p-[12px]'>
                    <div className='flex flex-col items-center justify-center space-y-[12px] cursor-pointer'>
                      <button
                        id='hours-swiper-back'
                        type='button'
                        className='disabled:opacity-50 bg-secondary rounded-[6px] w-[32px] h-[32px] flex justify-center items-center'
                      >
                        <IconDirectionDown className='rotate-180 h-[18px] w-[18px] text-primary' />
                      </button>
                      <Swiper
                        threshold={10}
                        draggable
                        loop
                        slideToClickedSlide
                        centeredSlides
                        mousewheel
                        freeMode={{
                          enabled: true,
                          sticky: true,
                        }}
                        direction='vertical'
                        slidesPerView={1}
                        initialSlide={valueWatch ? moment(valueWatch).hour() : 0}
                        navigation={{
                          prevEl: '#hours-swiper-back',
                          nextEl: '#hours-swiper-forward',
                        }}
                        modules={[Navigation, Mousewheel, FreeMode]}
                        className='w-[32px] h-[32px]'
                        onSlideChange={changeHour}
                        onSwiper={swiperHour}
                      >
                        {map(
                          map(range(0, 24), (numberItem) => padStart(toString(numberItem), 2, '0')),
                          (hour, hourIndex) => (
                            <SwiperSlide key={hourIndex}>
                              <span className='flex items-center justify-center leading-[23px] text-[18px] font-[600] w-[32px] h-[32px]'>
                                {hour}
                              </span>
                            </SwiperSlide>
                          ),
                        )}
                      </Swiper>
                      <button
                        id='hours-swiper-forward'
                        type='button'
                        className='disabled:opacity-50 bg-secondary rounded-[6px] w-[32px] h-[32px] flex justify-center items-center'
                      >
                        <IconDirectionDown className='h-[18px] w-[18px] text-primary' />
                      </button>
                    </div>
                    <span className='leading-[23px] text-[18px] font-[600]]'>:</span>
                    <div className='flex flex-col items-center justify-center space-y-[12px] cursor-pointer'>
                      <button
                        id='minutes-swiper-back'
                        type='button'
                        className='disabled:opacity-50 bg-secondary rounded-[6px] w-[32px] h-[32px] flex justify-center items-center'
                      >
                        <IconDirectionDown className='rotate-180 h-[18px] w-[18px] text-primary' />
                      </button>
                      <Swiper
                        threshold={10}
                        draggable
                        loop
                        slideToClickedSlide
                        centeredSlides
                        mousewheel
                        freeMode={{
                          enabled: true,
                          sticky: true,
                        }}
                        direction='vertical'
                        slidesPerView={1}
                        initialSlide={valueWatch ? moment(valueWatch).minute() : 0}
                        navigation={{
                          prevEl: '#minutes-swiper-back',
                          nextEl: '#minutes-swiper-forward',
                        }}
                        modules={[Navigation, Mousewheel, FreeMode]}
                        className='w-[32px] h-[32px]'
                        onSlideChange={changeMinute}
                        onSwiper={swiperMinute}
                      >
                        {map(
                          map(range(0, 60), (numberItem) => padStart(toString(numberItem), 2, '0')),
                          (minute, minuteIndex) => (
                            <SwiperSlide key={minuteIndex}>
                              <span className='flex items-center justify-center leading-[23px] text-[18px] font-[600] w-[32px] h-[32px]'>
                                {minute}
                              </span>
                            </SwiperSlide>
                          ),
                        )}
                      </Swiper>
                      <button
                        id='minutes-swiper-forward'
                        type='button'
                        className='disabled:opacity-50 bg-secondary rounded-[6px] w-[32px] h-[32px] flex justify-center items-center'
                      >
                        <IconDirectionDown className='h-[18px] w-[18px] text-primary' />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </fieldset>
      </div>
      {error && (
        <div className='flex w-full py-[3px] px-[12px] '>
          <span className='text-[12px] leading-[15px] text-alert'>{error?.message}</span>
        </div>
      )}
    </div>
  );
};

export default FormDatetime;
